import type { ReactNode } from "react";
import { Navigate, Outlet } from "react-router";
import { useProfile } from "../hooks/use-profile";
import type { Action, Subject } from "./ability";
import { useAbility } from "./ability-provider";

interface RequireAbilityProps {
  I: Action;
  a: Subject;
  /** Rendered instead of redirecting when the action is not allowed. */
  fallback?: ReactNode;
  /** Shown while the profile loads. */
  loading?: ReactNode;
  redirectTo?: string;
  children?: ReactNode;
}

/** Route guard: `<RequireAbility I="manage" a="Manager" fallback={<RestrictedAccess />} />` */
export function RequireAbility({
  I,
  a,
  fallback,
  loading = null,
  redirectTo = "/staff",
  children,
}: RequireAbilityProps) {
  const { isLoading } = useProfile();
  const ability = useAbility();

  // The ability is empty until the profile loads; deciding now would redirect
  // an admin who refreshed the page.
  if (isLoading) return <>{loading}</>;

  if (!ability.can(I, a)) {
    if (fallback) return <>{fallback}</>;
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
